<template>
  <!-- Leyendas colapsables de los criterios (*), (**) y (***) que referencian las tablas. -->
  <div class="legends">
    <!-- (*) Criterio de semáforo -->
    <section class="leg">
      <button class="leg-head" :aria-expanded="open.sem" @click="open.sem = !open.sem">
        <b-icon pack="fas" icon="chevron-right" size="is-small" :class="{ rot: open.sem }"></b-icon>
        <span class="leg-title">{{ $t('prm_portfolio_leg_sem_title') }}</span>
        <span class="leg-toggle">{{ open.sem ? $t('prm_portfolio_leg_hide') : $t('prm_portfolio_leg_show') }}</span>
      </button>
      <div v-if="open.sem" class="leg-body">
        <table class="leg-table">
          <thead>
            <tr>
              <th class="left">{{ $t('prm_dash_col_indicator') }}</th>
              <th class="left"><span class="dot" :style="{ background: SEM.green }"></span>{{ $t('prm_portfolio_col_green') }}</th>
              <th class="left"><span class="dot" :style="{ background: SEM.amber }"></span>{{ $t('prm_portfolio_col_amber') }}</th>
              <th class="left"><span class="dot" :style="{ background: SEM.red }"></span>{{ $t('prm_portfolio_col_red') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in semRows" :key="row.ind">
              <td class="left b">{{ $t(row.ind) }}</td>
              <td class="left">{{ $t(row.g) }}</td>
              <td class="left">{{ $t(row.a) }}</td>
              <td class="left">{{ $t(row.r) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- (**) Criterio: proyecto en situación crítica -->
    <section class="leg">
      <button class="leg-head" :aria-expanded="open.crit" @click="open.crit = !open.crit">
        <b-icon pack="fas" icon="chevron-right" size="is-small" :class="{ rot: open.crit }"></b-icon>
        <span class="leg-title">{{ $t('prm_portfolio_leg_crit_title') }}</span>
        <span class="leg-toggle">{{ open.crit ? $t('prm_portfolio_leg_hide') : $t('prm_portfolio_leg_show') }}</span>
      </button>
      <div v-if="open.crit" class="leg-body">
        <p class="leg-sub">{{ $t('prm_portfolio_leg_crit_subtitle') }}</p>
        <table class="leg-table">
          <thead>
            <tr>
              <th class="left">{{ $t('prm_portfolio_risk_criterion') }}</th>
              <th class="left">{{ $t('prm_portfolio_leg_crit_col') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in critRows" :key="row.area">
              <td class="left b"><span class="dot" :style="{ background: SEM.red }"></span>{{ $t(row.area) }}</td>
              <td class="left">{{ $t(row.c) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- (***) Criterio: matriz Impacto × Probabilidad -->
    <section class="leg">
      <button class="leg-head" :aria-expanded="open.risk" @click="open.risk = !open.risk">
        <b-icon pack="fas" icon="chevron-right" size="is-small" :class="{ rot: open.risk }"></b-icon>
        <span class="leg-title">{{ $t('prm_portfolio_leg_risk_title') }}</span>
        <span class="leg-toggle">{{ open.risk ? $t('prm_portfolio_leg_hide') : $t('prm_portfolio_leg_show') }}</span>
      </button>
      <div v-if="open.risk" class="leg-body">
        <p class="leg-sub">{{ $t('prm_portfolio_leg_risk_subtitle') }}</p>
        <div class="leg-grid">
          <table class="leg-table">
            <thead><tr><th class="left w60">{{ $t('prm_portfolio_leg_value') }}</th><th class="left">{{ $t('prm_portfolio_leg_desc') }}</th></tr></thead>
            <tbody>
              <tr v-for="n in 5" :key="'i' + n"><td class="left b">{{ $t('prm_portfolio_leg_impact') }} {{ n }}</td><td class="left">{{ $t('prm_portfolio_leg_imp_' + n) }}</td></tr>
            </tbody>
          </table>
          <table class="leg-table">
            <thead><tr><th class="left w60">{{ $t('prm_portfolio_leg_value') }}</th><th class="left">{{ $t('prm_portfolio_leg_prob') }}</th></tr></thead>
            <tbody>
              <tr v-for="n in 5" :key="'p' + n"><td class="left b">{{ n }}</td><td class="left">{{ $t('prm_portfolio_leg_prob_' + n) }}</td></tr>
            </tbody>
          </table>
        </div>
        <table class="leg-table">
          <thead>
            <tr>
              <th class="left w90">{{ $t('prm_portfolio_risk_situation') }}</th>
              <th class="left w130">{{ $t('prm_portfolio_leg_score_col') }}</th>
              <th class="left">{{ $t('prm_portfolio_leg_crit_col') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in scoreRows" :key="row.sem">
              <td class="left b"><span class="dot" :style="{ background: SEM[row.sem] }"></span>{{ $t('prm_portfolio_col_' + row.sem) }}</td>
              <td class="left">{{ row.range }}</td>
              <td class="left">{{ $t(row.c) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Matriz Impacto × Probabilidad: cada celda = I × P, coloreada por su nivel. -->
        <div class="mtx-wrap">
          <table class="mtx">
            <thead>
              <tr>
                <th class="mtx-axis">{{ $t('prm_portfolio_leg_matrix_axis') }}</th>
                <th v-for="p in 5" :key="'h' + p">{{ p }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in 5" :key="'mr' + i">
                <th class="mtx-row">{{ i }}</th>
                <td v-for="p in 5" :key="'mc' + i + '-' + p" :style="cellStyle(i * p)">{{ i * p }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import { SEM } from '../dashboard/dashboard-model'

  export default {
    name: 'PrmPortfolioCriteriaLegends',

    data: () => ({
      SEM,
      open: { sem: false, crit: false, risk: false },
      semRows: [
        { ind: 'prm_portfolio_grp_progress', g: 'prm_portfolio_leg_progress_g', a: 'prm_portfolio_leg_progress_a', r: 'prm_portfolio_leg_progress_r' },
        { ind: 'prm_portfolio_grp_margin', g: 'prm_portfolio_leg_margin_g', a: 'prm_portfolio_leg_margin_a', r: 'prm_portfolio_leg_margin_r' },
        { ind: 'prm_portfolio_col_state', g: 'prm_portfolio_leg_state_g', a: 'prm_portfolio_leg_state_a', r: 'prm_portfolio_leg_state_r' },
      ],
      critRows: [
        { area: 'prm_portfolio_crit_progress', c: 'prm_portfolio_leg_crit_progress' },
        { area: 'prm_portfolio_crit_margin', c: 'prm_portfolio_leg_crit_margin' },
        { area: 'prm_portfolio_crit_billing', c: 'prm_portfolio_leg_crit_billing' },
        { area: 'prm_portfolio_crit_contractual', c: 'prm_portfolio_leg_crit_contractual' },
        { area: 'prm_portfolio_crit_safety', c: 'prm_portfolio_leg_crit_safety' },
        { area: 'prm_portfolio_crit_quality', c: 'prm_portfolio_leg_crit_quality' },
      ],
      scoreRows: [
        { sem: 'green', range: '1 - 4', c: 'prm_portfolio_leg_score_g' },
        { sem: 'amber', range: '5 - 9', c: 'prm_portfolio_leg_score_a' },
        { sem: 'red', range: '≥ 10', c: 'prm_portfolio_leg_score_r' },
      ],
    }),

    methods: {
      // Celda de la matriz: coloreada por el nivel del score (≥10 rojo, ≥5 ámbar, resto verde).
      cellStyle (score) {
        const c = score >= 10 ? SEM.red : score >= 5 ? SEM.amber : SEM.green
        return { background: c + '22', color: c, fontWeight: 700 }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .legends { display: flex; flex-direction: column; gap: 12px; }
  .leg { background: #fff; border: 1px solid #aab6c5; border-radius: 6px; overflow: hidden; box-shadow: 0 1px 2px rgba(30, 48, 70, .08); }
  .leg-head {
    width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #fff; border: none;
    cursor: pointer; text-align: left;
  }
  .leg-head:hover { background: #f7f9fc; }
  .leg-head ::v-deep .icon { color: #3a6bc8; transition: transform 120ms; }
  .leg-head ::v-deep .icon.rot { transform: rotate(90deg); }
  .leg-title { flex: 1; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #1e3046; }
  .leg-toggle { font-size: 10px; color: #9aa4b2; }
  .leg-body { border-top: 1px solid #e5e7eb; padding: 10px 12px; }
  .leg-sub { font-size: 11px; color: #6b7787; margin-bottom: 8px; }
  .leg-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }

  .leg-table { width: 100%; border-collapse: collapse; }
  .leg-table thead th {
    background: #3d6b9a; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em;
    padding: 5px 8px; text-align: left; white-space: nowrap;
  }
  .leg-table td { border-bottom: 1px solid #eef0f3; padding: 5px 8px; font-size: 11px; color: #232c3d; vertical-align: top; }
  .leg-table tbody tr:nth-child(even) td { background: #f9fafb; }
  .leg-table td.b, .leg-table th.left { font-weight: 600; }
  .leg-table td.b { color: #1e3046; white-space: nowrap; }
  .w60 { width: 64px; } .w90 { width: 90px; } .w130 { width: 130px; }
  .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }

  // Matriz Impacto × Probabilidad.
  .mtx-wrap { margin-top: 12px; overflow-x: auto; }
  .mtx { border-collapse: collapse; font-size: 11px; }
  .mtx th {
    background: #3d6b9a; color: #fff; font-size: 10px; font-weight: 700; padding: 5px 10px; text-align: center;
  }
  .mtx th.mtx-axis { text-align: left; white-space: nowrap; }
  .mtx th.mtx-row { background: #3d6b9a; }
  .mtx td { border: 1px solid #eef0f3; padding: 6px 12px; text-align: center; font-variant-numeric: tabular-nums; }

  @media (max-width: 720px) { .leg-grid { grid-template-columns: 1fr; } }
</style>
